import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { LOCALES } from '../../common/constants';

interface RouteMap {
  function: string;
  howWorksInvozio: string;
  aboutInvozio: string;
  contactUs: string;
  startBusiness: string;
  dashboard: string;
  insurance: string;
  healthInsuranceMain: string;
  healthInsurance: string;
  privateCareInsurance: string;
  pension: string;
  freelanceConsultant: string;
  invoicingWithout2025: string;
  workingFreelancer: string;
  howMuchInvoice: string;
  fromInvoiceToNegotiation: string;
  business: string;
  selfEmployment: string;
  ambassador: string;
  unsubscribed: string;
  authorizeReminder: string;
  approvalNotify: string;
  freelanceFreelancing: string;
  invoicingIndividual: string;
  invoiceWithoutCompany: string;
  invoiceConsultant: string;
  news: string;
  bookKeeping: string;
  freelancingGuide: string;
  dashboardV2: string;
  profileSettings: string;
  faqDashboard: string;
}

interface UseLocalizedRoutesReturn {
  routes: RouteMap;
  getLocalizedPath: (routeKey: keyof RouteMap) => string;
  isEnglish: () => boolean;
  isSwedish: () => boolean;
  currentLanguage: string;
}

/**
 * Custom hook for managing localized routes
 * Returns route paths based on current language
 */
export const useLocalizedRoutes = (): UseLocalizedRoutesReturn => {
  const { i18n } = useTranslation();
  const currentLanguage = i18n.language;

  const routes = useMemo<RouteMap>(() => {
    const isEnglish = currentLanguage === LOCALES.EN_US;

    return {
      // Existing routes
      newsDetail: isEnglish ? "/news/:slug" : "/nyheter/:slug",
      function: isEnglish ? "/function" : "/funktion",
      howWorksInvozio: isEnglish ? "/how-works-invozio" : "/hur-fungerar-invozio",
      aboutInvozio: isEnglish ? "/about-invozio" : "/om-invozio",
      contactUs: isEnglish ? "/contact-us" : "/kontakta-oss",
      startBusiness: isEnglish
        ? "/start-your-own-business-or-invoice-as-a-private-person"
        : "/starta-eget-foretag-eller-fakturera-som-privatperson",
      dashboard: isEnglish ? "/dashboard/*" : "/instrumentbrada/*",
      insurance: isEnglish ? "/insurance" : "/forsakring",
      healthInsuranceMain: isEnglish ? "/health-insurance-main" : "/sjukforsakring-huvud",
      healthInsurance: isEnglish ? "/health-insurance" : "/sjukforsakring",
      privateCareInsurance: isEnglish ? "/private-healthcare-insurance" : "/privatvardsforsakring",
      pension: "/pension",
      freelanceConsultant: isEnglish
        ? "/freelance-consultant-salary-invoice-without-a-company"
        : "/frilans-konsult-lon-fakturera-utan-foretag",
      invoicingWithout2025: isEnglish
        ? "/invoicing-without-company-2025"
        : "/fakturering-utan-foretag-2025",
      workingFreelancer: isEnglish
        ? "/working-as-a-freelancer-and-invoicing-without-tax-registration"
        : "/att-jobba-som-frilansare-och-fakturera-utan-fskatt",
      howMuchInvoice: isEnglish
        ? "/how-much-should-I-invoice-as-a-freelancer"
        : "/hur-mycket-ska-jag-fakturera-som-frilansare",
      fromInvoiceToNegotiation: isEnglish
        ? "/from-invoice-to-negotiation"
        : "/fran-faktura-till-forhandling",
      business: isEnglish ? "/business" : "/foretag",
      selfEmployment: isEnglish ? "/self-employment" : "/egenanstallning",
      ambassador: "/ambassador", // Same for both languages
      unsubscribed: isEnglish ? "/unsubscribed" : "/avprenumererad",
      authorizeReminder: isEnglish ? "/authorize-reminder" : "/auktorisera-påminnelse",
      approvalNotify: isEnglish ? "/approval-notify" : "/godkannande-meddelande",
      freelanceFreelancing: isEnglish ? "/freelance-freelancing" : "/frilans-frilansarbete",
      invoicingIndividual: isEnglish
        ? "/invoicing-as-an-individual_self-employed"
        : "/fakturera-som-privatperson_egenanstalld",
      invoiceWithoutCompany: isEnglish
        ? "/invoice-without-company-or-business"
        : "/fakturera-utan-foretag-eller-bolag",
      invoiceConsultant: isEnglish ? "/invoice-as-consultant-salary" : "/fakturera-som-konsult-lon",

      news: isEnglish ? "/news" : "/nyheter",
      bookKeeping: isEnglish ? "/book-keeping" : "/bokforing",
      freelancingGuide: isEnglish ? "/freelance-freelancing" : "/frilans-frilansarbete",
      workConditions: isEnglish ? "/good-working-conditions" : "/bra-arbetsvillkor",
      dashboardV2: isEnglish ? "/v2/dashboard" : "/v2/dashboard",
      emailLogin: isEnglish ? "/emaillogin" : "/e-postinloggning",
      login: isEnglish ? "/login" : "/logga-in",
      forgotPassword: isEnglish ? "/forgot-password" : "/glomt-ditt-losenord",
      SetNewPassword: isEnglish ? "/set-new-password" : "/set-nytt-losenord",
      loyaltyProgram: isEnglish ? "/loyalty-program" : "/loyalty-program",
      profileSettings: isEnglish ? "/profile-settings" : "/profile-settings",
      manageinvoice: isEnglish ? "/v2/manage-invoice" : "/v2/manage-invoice",
      draftInvoice: isEnglish ? "/v2/draft-invoice" : "/v2/draft-invoice",
      myClients: isEnglish ? "/v2/my-clients" : "/v2/my-clients",
      myFreelancers: isEnglish ? "/v2/my-freelancers" : "/v2/my-freelancers",
      myContracts: isEnglish ? "/v2/my-contracts" : "/v2/my-contracts",
      myFreelancersCertificates: isEnglish ? "/v2/my-freelancers-certificates" : "/v2/my-freelancers-certificates",
      salaryNotice: isEnglish ? "/v2/salary-notice" : "/v2/salary-notice",
      HolidayPayReport: isEnglish ? "/v2/holiday-pay-report" : "/v2/holiday-pay-report",
      pensionReport: isEnglish ? "/v2/pension-report" : "/v2/pension-report",
      salaryCalculatorPage: isEnglish ? "/v2/salary-calculator" : "/v2/salary-calculator",
      healthInsurancePage: isEnglish ? "/v2/health-insurance" : "/v2/health-insurance",
      faqDashboard: isEnglish ? "/v2/faq" : "/v2/faq",
      createInvoice: isEnglish ? "/v2/create-invoice" : "/v2/create-invoice",
      deductionInvoice: isEnglish ? "/v2/deduction-invoice" : "/v2/deduction-invoice",
      statementsPage: isEnglish ? "/v2/statements-invoice" : "/v2/statements-invoice",
      timeSheetsPage: isEnglish ? "/v2/timesheets-invoice" : "/v2/timesheets-invoice",
    };
  }, [currentLanguage]);

  /**
   * Get localized path for a given route key
   * @param routeKey - The key of the route from the routes object
   * @returns The localized path
   */
  const getLocalizedPath = (routeKey: keyof RouteMap): string => {
    return routes[routeKey] || '/';
  };

  /**
   * Check if current language is English
   * @returns boolean
   */
  const isEnglish = (): boolean => {
    return currentLanguage === LOCALES.EN_US;
  };

  /**
   * Check if current language is Swedish
   * @returns boolean
   */
  const isSwedish = (): boolean => {
    return currentLanguage === LOCALES.SV_SE;
  };

  return {
    routes,
    getLocalizedPath,
    isEnglish,
    isSwedish,
    currentLanguage
  };
};
